import { apiClient } from "./apiClient";
import {
  DocumentImagesResponse,
  VerificationDetail,
  VerificationsResponse,
  VerificationsSummary,
} from "../types/verification";

export interface FetchVerificationsParams {
  /** 1-based page number, as the portal API expects. */
  page: number;
  pageSize: number;
  /** ISO date (YYYY-MM-DD), inclusive. Required by the API. */
  fromDate: string;
  /** ISO date (YYYY-MM-DD), inclusive. Required by the API. */
  toDate: string;
  status?: "Pending";
  /**
   * Jurisdiction name as returned in the summary's `availableJurisdictions`
   * (e.g. "Nevada"). Omitted entirely when nothing is selected, which the API
   * treats as "all jurisdictions".
   */
  jurisdiction?: string;
}

export interface FetchVerificationsSummaryParams {
  /** ISO date (YYYY-MM-DD), inclusive. Required by the API. */
  fromDate: string;
  /** ISO date (YYYY-MM-DD), inclusive. Required by the API. */
  toDate: string;
  /** See FetchVerificationsParams.jurisdiction. */
  jurisdiction?: string;
}

// Whole hours offset from UTC, standard sign convention (positive east of UTC,
// e.g. +5 for IST, -8 for PST) — the API requires an integer in [-12, 14].
// getTimezoneOffset() returns minutes *behind* UTC (opposite sign, and can be
// fractional for :30/:45 zones), so this negates and rounds to the nearest hour.
function getTimezoneOffsetHours(): number {
  return Math.round(-new Date().getTimezoneOffset() / 60);
}

export function fetchVerifications(params: FetchVerificationsParams) {
  return apiClient
    .get<VerificationsResponse>("/portal/api/verifications", {
      params: {
        ...params,
        timezoneOffset: getTimezoneOffsetHours(),
      },
    })
    .then((response) => response.data);
}

export function fetchVerificationsSummary(params: FetchVerificationsSummaryParams) {
  return apiClient
    .get<VerificationsSummary>("/portal/api/verifications/summary", {
      params: {
        ...params,
        timezoneOffset: getTimezoneOffsetHours(),
      },
    })
    .then((response) => response.data);
}

export function fetchVerificationById(verificationId: string): Promise<VerificationDetail> {
  return apiClient
    .get<VerificationDetail>(`/portal/api/verifications/${verificationId}`)
    .then((response) => response.data);
}

export function fetchDocumentImages(
  verificationId: string,
  documentId: string
): Promise<DocumentImagesResponse> {
  return apiClient
    .get<DocumentImagesResponse>(
      `/portal/api/verifications/${verificationId}/documents/${documentId}/images`
    )
    .then((response) => response.data);
}
